import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useClipboard,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiExternalLink } from 'react-icons/fi'
import { getChainByAlias, getChainByChainId } from '@shared/chainRegistryClient'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { monitoringService } from '@/services/admin/monitoringService'
import type { FactoryReadOnlySnapshot, PlatformContractDescriptor } from '@/types'
import { getExplorerAddressUrl, truncateAddress } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface ProgramMetadata {
  schemaVersion?: string
  idlAccount?: string
  platformConfig?: string
  feeTreasuryAuthority?: string
  programData?: string
}

interface ProgramDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  contract: PlatformContractDescriptor | null
  mode: NetworkEnvironment
}

const resolveChainName = (contract: PlatformContractDescriptor | null): string => {
  if (!contract) {
    return ''
  }
  const chain =
    getChainByChainId(Number(contract.chainId)) ??
    getChainByAlias(contract.chainKey) ??
    getChainByAlias(contract.chainCode)
  return chain?.name ?? contract.chainKey
}

const parseMetadata = (raw?: string | null): ProgramMetadata | null => {
  if (!raw?.trim()) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as ProgramMetadata
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

const AddressValue = ({
  address,
  chainId,
}: {
  address?: string | null
  chainId: number
}) => {
  const { t } = useTranslation()
  const value = address?.trim()
  const { hasCopied, onCopy } = useClipboard(value ?? '')
  const explorerUrl = useMemo(() => {
    if (!value) return undefined
    return getExplorerAddressUrl(chainId, value)
  }, [chainId, value])

  if (!value) {
    return <Text color="gray.500">{t('common.na')}</Text>
  }

  return (
    <HStack spacing={1} flexWrap="wrap">
      <Tooltip label={value} openDelay={300}>
        <Text fontFamily="mono" fontSize="sm">
          {truncateAddress(value)}
        </Text>
      </Tooltip>
      <Tooltip label={hasCopied ? t('common.copySuccess') : t('common.copy')}>
        <IconButton
          aria-label={t('common.copy')}
          icon={<FiCopy />}
          size="xs"
          variant="ghost"
          colorScheme={hasCopied ? 'green' : 'gray'}
          onClick={onCopy}
        />
      </Tooltip>
      {explorerUrl && (
        <Tooltip label={t('common.openExplorer')}>
          <IconButton
            aria-label={t('common.openExplorer')}
            icon={<FiExternalLink />}
            size="xs"
            variant="ghost"
            as="a"
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Tooltip>
      )}
    </HStack>
  )
}

const ValueRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" mb={1}>
      {label}
    </Text>
    {children}
  </Box>
)

export function ProgramDetailsModal({
  isOpen,
  onClose,
  contract,
  mode,
}: ProgramDetailsModalProps) {
  const { t } = useTranslation()
  const [snapshot, setSnapshot] = useState<FactoryReadOnlySnapshot | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const activeRequestRef = useRef(0)
  const chainName = useMemo(() => resolveChainName(contract), [contract])
  const metadata = useMemo(() => parseMetadata(contract?.metadata), [contract?.metadata])

  const loadSnapshot = useCallback(
    async () => {
      if (!contract) {
        return
      }

      const requestId = activeRequestRef.current + 1
      activeRequestRef.current = requestId
      const shouldApply = () => activeRequestRef.current === requestId

      setIsLoading(true)
      setError(null)

      try {
        const data = await monitoringService.getFactoryDetails(contract.id, mode)
        if (!shouldApply()) return
        setSnapshot(data)
      } catch (err: unknown) {
        if (!shouldApply()) return
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
      } finally {
        if (shouldApply()) {
          setIsLoading(false)
        }
      }
    },
    [contract, mode]
  )

  useEffect(() => {
    if (!isOpen || !contract) {
      setSnapshot(null)
      setError(null)
      setIsLoading(false)
      return
    }

    void loadSnapshot()

    return () => {
      activeRequestRef.current += 1
    }
  }, [contract, isOpen, loadSnapshot])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr={12}>
          <HStack align="flex-start" justify="space-between" spacing={3}>
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="semibold">
                {t('monitoring.platformContracts.program.title', {
                  defaultValue: 'Solana Program · {{chain}}',
                  chain: chainName || '--',
                })}
              </Text>
              <HStack spacing={2}>
                <Badge colorScheme={contract?.isTestnet ? 'purple' : 'blue'}>
                  {contract?.isTestnet
                    ? t('monitoring.platformContracts.testnet')
                    : t('monitoring.platformContracts.mainnet')}
                </Badge>
                {contract?.isActive !== undefined && (
                  <Badge colorScheme={contract.isActive ? 'green' : 'yellow'}>
                    {contract.isActive
                      ? t('monitoring.platformContracts.labels.active')
                      : t('monitoring.platformContracts.labels.inactive')}
                  </Badge>
                )}
                {contract?.version && <Badge>{contract.version}</Badge>}
                {metadata?.schemaVersion && <Badge>{`schema ${metadata.schemaVersion}`}</Badge>}
              </HStack>
            </VStack>
            <RefreshIconButton
              label={t('common.refresh')}
              tooltipPlacement="left"
              onClick={() => void loadSnapshot()}
              isLoading={isLoading}
              isDisabled={!contract || isLoading}
              flexShrink={0}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoading ? (
            <HStack justify="center" py={6}>
              <Spinner size="md" />
            </HStack>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <VStack align="stretch" spacing={5}>
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  {t('monitoring.platformContracts.program.sections.program', {
                    defaultValue: 'Program',
                  })}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <ValueRow
                    label={t('monitoring.platformContracts.program.fields.programId', {
                      defaultValue: 'Program ID',
                    })}
                  >
                    <AddressValue
                      address={snapshot?.programId ?? contract?.contractAddress}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                  <ValueRow
                    label={t('monitoring.platformContracts.program.fields.idlAccount', {
                      defaultValue: 'IDL Account',
                    })}
                  >
                    <AddressValue
                      address={snapshot?.idlAccount ?? metadata?.idlAccount}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                </SimpleGrid>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Platform Addresses
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <ValueRow label={t('monitoring.platformContracts.factory.fields.platformRefunder')}>
                    <AddressValue
                      address={snapshot?.platformRefunder}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                  <ValueRow label={t('monitoring.platformContracts.factory.fields.platformOperator')}>
                    <AddressValue
                      address={snapshot?.platformOperator}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                  <ValueRow label={t('monitoring.platformContracts.factory.fields.platformGovernor')}>
                    <AddressValue
                      address={snapshot?.platformGovernor}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                </SimpleGrid>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  {t('monitoring.platformContracts.program.sections.pdas', {
                    defaultValue: 'Program Metadata',
                  })}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <ValueRow
                    label={t('monitoring.platformContracts.program.fields.platformConfig', {
                      defaultValue: 'Platform Config',
                    })}
                  >
                    <AddressValue
                      address={snapshot?.platformConfig ?? metadata?.platformConfig}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                  <ValueRow
                    label={t('monitoring.platformContracts.program.fields.feeTreasuryAuthority', {
                      defaultValue: 'Fee Treasury Authority',
                    })}
                  >
                    <AddressValue
                      address={snapshot?.feeTreasuryAuthority ?? metadata?.feeTreasuryAuthority}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                  <ValueRow
                    label={t('monitoring.platformContracts.program.fields.programData', {
                      defaultValue: 'Program Data',
                    })}
                  >
                    <AddressValue
                      address={snapshot?.programData ?? metadata?.programData}
                      chainId={contract?.chainId ?? 0}
                    />
                  </ValueRow>
                </SimpleGrid>
              </Box>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default ProgramDetailsModal
